import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './BotLogs.css';

const LEVELS = [
  { value: 'all', label: 'Tous les niveaux' },
  { value: 'success', label: 'Succès' },
  { value: 'error', label: 'Erreurs' },
  { value: 'warning', label: 'Avertissements' },
  { value: 'info', label: 'Info' },
];

const today = () => new Date().toISOString().slice(0, 10);

const levelBadge = (level) => {
  const map = {
    success: { bg: '#d1fae5', color: '#065f46', label: '✔ Succès' },
    error:   { bg: '#fee2e2', color: '#991b1b', label: '✖ Erreur' },
    warning: { bg: '#fef3c7', color: '#92400e', label: '⚠ Warning' },
    info:    { bg: '#dbeafe', color: '#1e40af', label: 'ℹ Info' },
  };
  const s = map[level] || map.info;
  return <span className="bot-logs-badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};

const BotLogs = () => {
  const { token } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshRef = useRef(null);

  // Filtres
  const [filters, setFilters] = useState({
    config_id: '',
    level: 'all',
    symbol: '',
    date_from: today(),
    date_to: today(),
    limit: '200',
    offset: '0',
  });

  const headers = useCallback(() =>
    token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  // Charger les configs de bot disponibles
  useEffect(() => {
    fetch('/api/alpaca/config/all', { headers: headers() })
      .then(r => r.json())
      .then(d => { if (d.configs) setConfigs(d.configs); })
      .catch(() => {});
  }, [headers]);

  const fetchLogs = useCallback(async (f = filters) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (f.config_id) params.set('config_id', f.config_id);
      if (f.level && f.level !== 'all') params.set('level', f.level);
      if (f.symbol.trim()) params.set('symbol', f.symbol.trim());
      if (f.date_from) params.set('date_from', f.date_from);
      if (f.date_to) params.set('date_to', f.date_to);
      params.set('limit', f.limit);
      params.set('offset', f.offset);

      const res = await fetch(`/api/bot/db-logs?${params}`, { headers: headers() });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Erreur');
      setLogs(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers, filters]);

  // Chargement initial et filtre
  useEffect(() => { fetchLogs(filters); }, []); // eslint-disable-line

  // Auto-refresh
  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => fetchLogs(filters), 10000);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [autoRefresh, fetchLogs, filters]);

  const handleFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: '0' }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs({ ...filters, offset: '0' });
  };

  const handlePage = (dir) => {
    const limit = parseInt(filters.limit);
    const offset = Math.max(0, parseInt(filters.offset) + dir * limit);
    const newFilters = { ...filters, offset: String(offset) };
    setFilters(newFilters);
    fetchLogs(newFilters);
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' });
  };

  const configName = (id) => {
    if (!id) return '—';
    const c = configs.find(c => String(c.id) === String(id));
    return c ? (c.sub_account_name || c.account_type || `Bot #${id}`) : `Bot #${id}`;
  };

  const page = Math.floor(parseInt(filters.offset) / parseInt(filters.limit)) + 1;
  const totalPages = Math.ceil(total / parseInt(filters.limit));

  return (
    <div className="bot-logs-page">
      <div className="bot-logs-header">
        <div>
          <h1 className="bot-logs-title">📋 Logs des bots</h1>
          <p className="bot-logs-subtitle">Historique persistant des actions de trading</p>
        </div>
        <label className="bot-logs-autorefresh">
          <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          Auto-refresh (10s)
        </label>
      </div>

      {/* Filtres */}
      <form className="bot-logs-filters" onSubmit={handleSearch}>
        <select value={filters.config_id} onChange={e => handleFilter('config_id', e.target.value)}>
          <option value="">Tous les bots</option>
          {configs.map(c => (
            <option key={c.id} value={c.id}>
              {c.sub_account_name || c.account_type || `Bot #${c.id}`}
            </option>
          ))}
        </select>

        <select value={filters.level} onChange={e => handleFilter('level', e.target.value)}>
          {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>

        <input
          type="text"
          placeholder="Symbole (ex: AAPL)"
          value={filters.symbol}
          onChange={e => handleFilter('symbol', e.target.value)}
        />

        <input type="date" value={filters.date_from} onChange={e => handleFilter('date_from', e.target.value)} />
        <span className="bot-logs-sep">→</span>
        <input type="date" value={filters.date_to} onChange={e => handleFilter('date_to', e.target.value)} />

        <button type="submit" className="btn-search" disabled={loading}>
          {loading ? '⏳' : '🔍 Rechercher'}
        </button>
        <button type="button" className="btn-refresh" onClick={() => fetchLogs(filters)} disabled={loading} title="Actualiser">
          🔄
        </button>
      </form>

      {error && <div className="bot-logs-error">{error}</div>}

      {/* Stats */}
      <div className="bot-logs-stats">
        <span>{total.toLocaleString('fr-FR')} entrée(s) trouvée(s)</span>
        {totalPages > 1 && <span>Page {page}/{totalPages}</span>}
      </div>

      {/* Table */}
      <div className="bot-logs-table-wrap">
        <table className="bot-logs-table">
          <thead>
            <tr>
              <th>Heure</th>
              <th>Bot</th>
              <th>Niveau</th>
              <th>Symbole</th>
              <th>Message</th>
              <th>Détails</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && !loading && (
              <tr><td colSpan={6} className="bot-logs-empty">Aucun log pour ces critères</td></tr>
            )}
            {logs.map(log => (
              <LogRow key={log.id} log={log} configName={configName} formatTime={formatTime} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bot-logs-pagination">
          <button onClick={() => handlePage(-1)} disabled={parseInt(filters.offset) === 0}>← Précédent</button>
          <span>Page {page} / {totalPages}</span>
          <button onClick={() => handlePage(1)} disabled={page >= totalPages}>Suivant →</button>
        </div>
      )}
    </div>
  );
};

const LogRow = ({ log, configName, formatTime }) => {
  const [expanded, setExpanded] = useState(false);
  let details = null;
  try { details = log.data ? (typeof log.data === 'string' ? JSON.parse(log.data) : log.data) : null; } catch (_) {}

  return (
    <tr className={`bot-logs-row bot-logs-row--${log.log_level}`}>
      <td className="bot-logs-time">{formatTime(log.created_at)}</td>
      <td className="bot-logs-config">{configName(log.config_id)}</td>
      <td>{levelBadge(log.log_level)}</td>
      <td className="bot-logs-symbol">{log.symbol ? <code>{log.symbol}</code> : '—'}</td>
      <td className="bot-logs-message">{log.message}</td>
      <td>
        {details && (
          <button className="btn-details" onClick={() => setExpanded(!expanded)}>
            {expanded ? '▲' : '▼'}
          </button>
        )}
        {expanded && details && (
          <pre className="bot-logs-details">{JSON.stringify(details, null, 2)}</pre>
        )}
      </td>
    </tr>
  );
};

export default BotLogs;
